import type { Charge } from "@/pages/sy/models/Charge";
import type { ChargeLayer } from "@/pages/sy/models/ChargeLayer";
import type { RecipeLayer } from "@/pages/sy/models/RecipeLayer";

const WEIGHT_INCREMENT = 10;
const ADJUSTABLE_STATUSES = new Set(["O", "S"]);

export interface ScaledChargeTarget {
  originalTotalTargetWeight: number;
  scaledTotalTargetWeight: number;
}

export interface HeatWeightScaling {
  requestedHeatWeight: number;
  closedActualWeight: number;
  scaledRemainingTotalWeight: number;
  targetsByChargeId: ReadonlyMap<number, ScaledChargeTarget>;
}

export interface RequestedWeightLayerSuggestions {
  targetWeightsByRecipeLayerId: ReadonlyMap<number, number>;
  remainingWeightsByRecipeLayerId: ReadonlyMap<number, number>;
}

const roundToIncrement = (weight: number) =>
  Math.round(weight / WEIGHT_INCREMENT) * WEIGHT_INCREMENT;

const roundWeightsToTotal = (
  weights: number[],
  targetTotal: number,
): number[] => {
  if (weights.length === 0) return [];

  const targetUnits = Math.round(targetTotal / WEIGHT_INCREMENT);
  const rawUnits = weights.map((weight) =>
    Math.max(0, weight / WEIGHT_INCREMENT),
  );
  const roundedUnits = rawUnits.map(Math.floor);
  let unitsToDistribute =
    targetUnits - roundedUnits.reduce((sum, units) => sum + units, 0);
  const remainderOrder = rawUnits
    .map((units, index) => ({
      index,
      remainder: units - Math.floor(units),
    }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);

  for (let index = 0; unitsToDistribute > 0; index += 1) {
    roundedUnits[remainderOrder[index % remainderOrder.length].index] += 1;
    unitsToDistribute -= 1;
  }

  return roundedUnits.map((units) => units * WEIGHT_INCREMENT);
};

const distributeEqualDifference = (
  originalWeights: number[],
  targetTotal: number,
): number[] => {
  const distributedWeights = originalWeights.map(() => 0);
  let adjustableIndexes = originalWeights.map((_, index) => index);

  while (adjustableIndexes.length > 0) {
    const originalAdjustableTotal = adjustableIndexes.reduce(
      (sum, index) => sum + originalWeights[index],
      0,
    );
    const equalDifference =
      (targetTotal - originalAdjustableTotal) / adjustableIndexes.length;
    const belowZeroIndexes = adjustableIndexes.filter(
      (index) => originalWeights[index] + equalDifference < 0,
    );

    if (belowZeroIndexes.length === 0) {
      adjustableIndexes.forEach((index) => {
        distributedWeights[index] = originalWeights[index] + equalDifference;
      });
      break;
    }

    const belowZeroSet = new Set(belowZeroIndexes);
    belowZeroIndexes.forEach((index) => {
      distributedWeights[index] = 0;
    });
    adjustableIndexes = adjustableIndexes.filter(
      (index) => !belowZeroSet.has(index),
    );
  }

  return roundWeightsToTotal(distributedWeights, targetTotal);
};

export const calculateHeatWeightScaling = (
  charges: Charge[],
): HeatWeightScaling | null => {
  const heatWeightRequest = charges.find(
    (charge) => charge.RequestedHeatWeight != null,
  );
  const requestedHeatWeight = heatWeightRequest?.RequestedHeatWeight;

  if (requestedHeatWeight == null || requestedHeatWeight <= 0) return null;

  const chargesWithIds = charges
    .filter((charge): charge is Charge & { ChargeId: number } =>
      Number.isInteger(charge.ChargeId),
    )
    .sort(
      (a, b) =>
        Number(a.ChargeNumber ?? Number.MAX_SAFE_INTEGER) -
        Number(b.ChargeNumber ?? Number.MAX_SAFE_INTEGER),
    );

  const closedActualWeight = charges
    .filter((charge) => !ADJUSTABLE_STATUSES.has(charge.ChargeStatusCode))
    .reduce((sum, charge) => sum + Number(charge.TotalWeight ?? 0), 0);
  const scaledRemainingTotalWeight = roundToIncrement(
    Math.max(0, Number(requestedHeatWeight) - closedActualWeight),
  );
  const targetsByChargeId = new Map<number, ScaledChargeTarget>();
  const requestTimestamp = Date.parse(
    heatWeightRequest?.HeatWeightRequestCreationTimestamp ?? "",
  );

  const isClosedAfterRequest = (charge: Charge) => {
    if (ADJUSTABLE_STATUSES.has(charge.ChargeStatusCode)) return false;

    const closeTimestamp = Date.parse(charge.CloseTimestamp ?? "");
    return (
      Number.isFinite(requestTimestamp) &&
      Number.isFinite(closeTimestamp) &&
      closeTimestamp > requestTimestamp
    );
  };

  const affectedCharges = chargesWithIds.filter(
    (charge) =>
      ADJUSTABLE_STATUSES.has(charge.ChargeStatusCode) ||
      isClosedAfterRequest(charge),
  );

  if (affectedCharges.length === 0) return null;

  let committedActualWeight = chargesWithIds
    .filter(
      (charge) =>
        !ADJUSTABLE_STATUSES.has(charge.ChargeStatusCode) &&
        !isClosedAfterRequest(charge),
    )
    .reduce((sum, charge) => sum + Number(charge.TotalWeight ?? 0), 0);
  let remainingCharges = [...affectedCharges];

  const calculateRemainingTargets = () => {
    const targetWeight = roundToIncrement(
      Math.max(0, Number(requestedHeatWeight) - committedActualWeight),
    );
    const originalTargets = remainingCharges.map((charge) =>
      Math.max(0, Number(charge.ManualTargetWeight ?? 0)),
    );
    const scaledTargets = distributeEqualDifference(
      originalTargets,
      targetWeight,
    );

    return new Map(
      remainingCharges.map((charge, index) => [
        charge.ChargeId,
        {
          originalTotalTargetWeight: originalTargets[index],
          scaledTotalTargetWeight: scaledTargets[index],
        },
      ]),
    );
  };

  const closedAffectedCharges = affectedCharges
    .filter((charge) => !ADJUSTABLE_STATUSES.has(charge.ChargeStatusCode))
    .sort((a, b) => {
      const timestampDifference =
        Date.parse(a.CloseTimestamp ?? "") - Date.parse(b.CloseTimestamp ?? "");
      return timestampDifference || a.ChargeId - b.ChargeId;
    });

  closedAffectedCharges.forEach((closedCharge) => {
    const currentTargets = calculateRemainingTargets();
    const closedTarget = currentTargets.get(closedCharge.ChargeId);
    if (closedTarget) {
      targetsByChargeId.set(closedCharge.ChargeId, closedTarget);
    }

    committedActualWeight += Number(closedCharge.TotalWeight ?? 0);
    remainingCharges = remainingCharges.filter(
      (charge) => charge.ChargeId !== closedCharge.ChargeId,
    );
  });

  calculateRemainingTargets().forEach((target, chargeId) => {
    targetsByChargeId.set(chargeId, target);
  });

  return {
    requestedHeatWeight: Number(requestedHeatWeight),
    closedActualWeight,
    scaledRemainingTotalWeight,
    targetsByChargeId,
  };
};

type WeightedRecipeLayer = RecipeLayer & { RecipeLayerId: number };

const getWeightedRecipeLayers = (
  recipeLayers: RecipeLayer[],
): WeightedRecipeLayer[] =>
  recipeLayers.filter(
    (layer): layer is WeightedRecipeLayer =>
      layer.RecipeLayerId != null && Number(layer.Weight ?? 0) > 0,
  );

export const calculateScaledRecipeLayerWeights = (
  recipeLayers: RecipeLayer[],
  scaledTargetWeight: number,
): ReadonlyMap<number, number> | null => {
  const weightedRecipeLayers = getWeightedRecipeLayers(recipeLayers);
  const originalTotal = weightedRecipeLayers.reduce(
    (sum, layer) => sum + Number(layer.Weight ?? 0),
    0,
  );

  if (weightedRecipeLayers.length === 0 || originalTotal <= 0) return null;

  const proportionalWeights = weightedRecipeLayers.map(
    (layer) => (Number(layer.Weight ?? 0) / originalTotal) * scaledTargetWeight,
  );
  const roundedWeights = roundWeightsToTotal(
    proportionalWeights,
    scaledTargetWeight,
  );
  const scaledWeightsByRecipeLayerId = new Map<number, number>();

  weightedRecipeLayers.forEach((layer, index) => {
    scaledWeightsByRecipeLayerId.set(
      layer.RecipeLayerId,
      roundedWeights[index],
    );
  });

  return scaledWeightsByRecipeLayerId;
};

export const findMatchingRecipeLayer = (
  chargeLayer: ChargeLayer,
  recipeLayers: RecipeLayer[],
) => {
  if (chargeLayer.RecipeLayerId != null) {
    const linkedRecipeLayer = recipeLayers.find(
      (recipeLayer) => recipeLayer.RecipeLayerId === chargeLayer.RecipeLayerId,
    );
    if (linkedRecipeLayer?.CommodityId === chargeLayer.CommodityId) {
      return linkedRecipeLayer;
    }
  }

  return recipeLayers.find(
    (recipeLayer) =>
      recipeLayer.CommodityId === chargeLayer.CommodityId &&
      recipeLayer.LayerNumber === chargeLayer.LayerNumber,
  );
};

export const calculateRequestedWeightLayerSuggestions = (
  recipeLayers: RecipeLayer[],
  chargeLayers: ChargeLayer[],
  scaledTargetWeight: number,
): RequestedWeightLayerSuggestions | null => {
  const suggestedRecipeLayerIds = new Set(
    getWeightedRecipeLayers(recipeLayers).map((layer) => layer.RecipeLayerId),
  );
  if (suggestedRecipeLayerIds.size === 0) return null;

  const actualWeightsByRecipeLayerId = new Map<number, number>();
  let excludedActualWeight = 0;

  chargeLayers.forEach((chargeLayer) => {
    const actualWeight = Number(chargeLayer.Weight ?? 0);
    const recipeLayer = findMatchingRecipeLayer(chargeLayer, recipeLayers);
    const recipeLayerId = recipeLayer?.RecipeLayerId;

    if (recipeLayerId == null || !suggestedRecipeLayerIds.has(recipeLayerId)) {
      if (actualWeight > 0) {
        excludedActualWeight += actualWeight;
      }
      return;
    }

    actualWeightsByRecipeLayerId.set(
      recipeLayerId,
      (actualWeightsByRecipeLayerId.get(recipeLayerId) ?? 0) + actualWeight,
    );
  });

  const remainingTargetWeight = roundToIncrement(
    Math.max(0, scaledTargetWeight - excludedActualWeight),
  );
  const targetWeightsByRecipeLayerId = calculateScaledRecipeLayerWeights(
    recipeLayers,
    remainingTargetWeight,
  );
  if (!targetWeightsByRecipeLayerId) return null;

  return {
    targetWeightsByRecipeLayerId,
    remainingWeightsByRecipeLayerId: new Map(
      [...targetWeightsByRecipeLayerId].map(([recipeLayerId, targetWeight]) => [
        recipeLayerId,
        roundToIncrement(
          targetWeight - (actualWeightsByRecipeLayerId.get(recipeLayerId) ?? 0),
        ),
      ]),
    ),
  };
};
